// ---------------------------------------------------------------------------
// Tutorial v2 — the step machine
// ---------------------------------------------------------------------------
//
// Every step of TUTORIAL_V2_DESIGN.md, and a pure reducer that walks them.
// No React: useTutorialV2 holds the state and plays each round's reveal; the
// rules of what the player may do at each step live here, where a test can
// drive the whole tutorial without a screen.
//
// A step is one of:
//   next    the player reads, then taps Next
//   act     the player must do something on the board; anything else is
//           refused with a tip. The step ends when `done(state)` holds.
//           A deploy step lists the round's fresh drones per corridor up to
//           and including itself (R5's second deploy step lists [1, 5]).
//   peek    the player holds the eye
//   submit  the player submits: the round resolves through the real rules
//   end     the war is won
//
// Corridors are numbered 1–7, as the player sees them.

import { deployArmy, moveUnit, buildFort, canAttack, requiredDropZones, canPlaceDropZone } from '../rules/gameLogic'
import { canPlaceBase } from '../rules/subplotBases'
import { plotOf, subIndex } from '../rules/subplotGrid'
import { ARMIES_PER_ROUND, BATTLEFIELD_COUNT } from '../rules/gameConfig'
import { TUTORIAL_V2_TARGETS, createTutorialV2War, playTutorialV2RoundWith } from './tutorialV2'

const noTargets = () => Array(BATTLEFIELD_COUNT).fill(null)
export const fresh = (state, corridor) => state.board[corridor - 1].newArmies || 0
const freshPlanned = (step, corridor) => (step.deploy ?? []).filter(c => c === corridor).length
export const deploysDone = (state) => state.armiesToDeploy === 0
export const aimed = (corridor) => (state) => state.targetSubplots[corridor - 1] != null
export const stampAt = (hf, s) => hf?.subplots?.[s]
export const letterDistance = (hf, s) => {
    const c = stampAt(hf, s)
    return c?.distance != null ? `${c.distanceLetter}:${c.distance}` : null
}

// ── The steps ───────────────────────────────────────────────────────────────
// `round` is the tutorial round (1–8) the step belongs to; `highlight` the
// corridors the screen lights; `aims` the key into TUTORIAL_V2_TARGETS, the
// one hex the player must aim at (the screen points an arrow at it).
export const TUTORIAL_V2_STEPS = [
    // ── R1 ──
    { id: 'hive', round: 1, kind: 'next', selfXray: true, outlineHive: true,
      text: 'This is your Hive. The blue hexagons are your structures.' },
    { id: 'falls', round: 1, kind: 'next', selfXray: true,
      text: 'A structure is destroyed when each of its hex parts is destroyed. A partially damaged structure is not dead yet.' },
    { id: 'hidden', round: 1, kind: 'next',
      text: 'Your opponent can’t see where your structures are hidden, and you can’t see theirs. You each have 7 structures, and must find and kill 3 of your opponent’s before they kill yours.' },
    { id: 'slices', round: 1, kind: 'next', sweepCorridors: true,
      text: 'The 7 corridors run vertically over both Hives. Players build up forces in corridors, then attack when opportunity arises.' },
    { id: 'deploy1', round: 1, kind: 'act', highlight: [3, 4], deploy: [3, 4], done: deploysDone,
      text: 'You get 2 drones to place each round. Tap corridors 3 and 4 to deploy them.' },
    { id: 'notYet', round: 1, kind: 'next',
      text: 'Drones can only attack from a corridor they were already in when the round started. These just arrived, so nobody can attack this round.' },
    { id: 'submit1', round: 1, kind: 'submit',
      text: 'Tap Submit to end the round.' },

    // ── R2 ──
    { id: 'paid', round: 2, kind: 'next',
      text: 'Round 2. Your drones flew up into the airspace.' },
    { id: 'noAttacks', round: 2, kind: 'next',
      text: 'No one can attack until round 3. Use this round to build up your forces.' },
    { id: 'deploy2', round: 2, kind: 'act', highlight: [5], deploy: [5, 5], done: deploysDone,
      text: 'Deploy both new drones to corridor 5.' },
    { id: 'submit2', round: 2, kind: 'submit',
      text: 'Submit.' },

    // ── R3 ──
    { id: 'attacksOpen', round: 3, kind: 'next',
      text: 'Round 3: attacks are open. Drones that began this round in a corridor can attack from it.' },
    { id: 'theirRule', round: 3, kind: 'next', highlight: [2, 6],
      text: 'The same rule holds for your opponent. They can only attack from corridors where they had drones at the start of this round: here, corridors 2 and 6.' },
    { id: 'attack3', round: 3, kind: 'act', highlight: [3], attack: 3, done: (s) => s.board[2].isAttacking,
      text: 'Attack from corridor 3: swipe up on it, or tap its arrow.' },
    { id: 'aim3', round: 3, kind: 'act', highlight: [3], aims: 'probe1', aimCorridor: 3, done: aimed(3),
      text: 'Choose your target: tap the hex the arrow points to.' },
    { id: 'attack4', round: 3, kind: 'act', highlight: [4], attack: 4, done: (s) => s.board[3].isAttacking,
      text: 'You can attack from more than one corridor in a round. Attack from corridor 4 as well.' },
    { id: 'aim4', round: 3, kind: 'act', highlight: [4], aims: 'probe2', aimCorridor: 4, done: aimed(4),
      text: 'Tap the hex the arrow points to.' },
    { id: 'deploy3', round: 3, kind: 'act', highlight: [7], deploy: [7, 7], done: deploysDone,
      text: 'Deploy both drones to corridor 7.' },
    { id: 'submit3', round: 3, kind: 'submit',
      text: 'Submit.' },
    { id: 'probe1', round: 3, kind: 'next',
      text: (s) => `No structure there, but each strike left a clue. ${letterDistance(s.war.p2.homefront, s.aims.probe1) ?? 'A:6'} means structure A is ${stampAt(s.war.p2.homefront, s.aims.probe1)?.distance ?? 6} hexes from where you hit in corridor 3. ${letterDistance(s.war.p2.homefront, s.aims.probe2) ?? 'A:3'} means it’s ${stampAt(s.war.p2.homefront, s.aims.probe2)?.distance ?? 3} hexes from where you hit in corridor 4.` },
    { id: 'peek1', round: 3, kind: 'peek',
      text: 'Hold the eye at the top to see your clues.' },
    { id: 'ring1', round: 3, kind: 'next',
      text: 'Each ring is every hex at that clue’s distance. Hold the eye anytime to see the clues on the surface of each Hive.' },
    { id: 'cross', round: 3, kind: 'next',
      text: 'Structure A is on both rings, so it has to be where they cross. Only one hex is on both.' },

    // ── R4 ──
    { id: 'attack5', round: 4, kind: 'act', highlight: [5], attack: 5, done: (s) => s.board[4].isAttacking,
      text: 'Round 4. The hex where the clues intersect is in corridor 5. Attack from corridor 5.' },
    { id: 'aim5', round: 4, kind: 'act', highlight: [5], aims: 'kill', aimCorridor: 5, done: aimed(5),
      text: 'Aim where the rings cross.' },
    { id: 'deploy4', round: 4, kind: 'act', highlight: [6], deploy: [6, 6], done: deploysDone,
      text: 'Deploy both new drones to corridor 6.' },
    { id: 'submit4', round: 4, kind: 'submit',
      text: 'Submit.' },
    { id: 'killA', round: 4, kind: 'next',
      text: 'Structure A is destroyed: one down, two to go.\n\nEvery successful strike on your opponent’s Hive points you to the nearest live structure. The new clue points at structure B.' },
    { id: 'struck', round: 4, kind: 'next',
      text: 'Your opponent struck your Hive too. They now know where the remaining segments of that damaged structure are.' },

    // ── R5 ──
    { id: 'block', round: 5, kind: 'act', highlight: [1], deploy: [1], done: (s) => fresh(s, 1) === 1,
      text: 'Round 5. They have a drone in corridor 1, so they can attack from there this round. Block it by placing a drone in corridor 1. New drones defend straight away.' },
    { id: 'moreDrones', round: 5, kind: 'next',
      text: 'But how do you get more drones?\n\nBuild broods.\n\nIt takes 2 mature drones in the same corridor to build a brood.' },
    { id: 'brood', round: 5, kind: 'act', highlight: [7], brood: 7, done: (s) => (s.board[6].pendingBaseBuilds ?? []).length === 1,
      text: 'Drag a drone in corridor 7 onto the other, then slide the brood in place somewhere in that same corridor. The drones will then fly down and start to build.' },
    { id: 'broodInfo', round: 5, kind: 'next',
      text: 'Your brood is under construction. It can’t fight yet, a single hit would destroy it, and the enemy can see it.' },
    { id: 'deploy5', round: 5, kind: 'act', highlight: [5], deploy: [1, 5], done: deploysDone,
      text: 'Deploy your last drone to corridor 5.' },
    { id: 'submit5', round: 5, kind: 'submit',
      text: 'Submit.' },
    { id: 'tie', round: 5, kind: 'next',
      text: 'They attacked corridor 1: 1 against 1 is a tie. Both sides are wiped, and no strike lands.' },

    // ── R6 ──
    { id: 'setup', round: 6, kind: 'next', highlight: [5, 6],
      text: 'Round 6. You have 1 veteran in corridor 5 and 2 in corridor 6. They have 1 in corridor 5, so attacking now would be a tie.' },
    { id: 'move', round: 6, kind: 'act', highlight: [6], move: { from: 6, dir: 'left' }, done: (s) => s.board[5].movedOut,
      text: 'Drag a corridor 6 veteran left into corridor 5. Drones that move still fight.' },
    { id: 'oneMove', round: 6, kind: 'next',
      text: 'Each corridor can send out only one drone a round, so corridor 6 can’t move again. Only veterans move, one corridor left or right.' },
    { id: 'attack5b', round: 6, kind: 'act', highlight: [5], attack: 5, done: (s) => s.board[4].isAttacking,
      text: 'Corridor 5 is 2 against 1 now. Attack from it.' },
    { id: 'aim5b', round: 6, kind: 'act', highlight: [5], aims: 'damage', aimCorridor: 5, done: aimed(5),
      text: 'B is close to corridor 5’s edge. Tap the hex the arrow points to.' },
    { id: 'deploy6', round: 6, kind: 'act', highlight: [7], deploy: [7, 7], done: deploysDone,
      text: 'Deploy both new drones to corridor 7, to defend your brood that is still being built.' },
    { id: 'submit6', round: 6, kind: 'submit',
      text: 'Submit.' },
    { id: 'damaged', round: 6, kind: 'next',
      text: 'You hit structure B. It’s damaged, not destroyed.' },

    // ── R7 ──
    { id: 'deploy7', round: 7, kind: 'act', highlight: [3, 4], deploy: [3, 4], done: deploysDone,
      text: 'Round 7. Deploy to corridors 3 and 4.' },
    { id: 'mature', round: 7, kind: 'next', highlight: [7],
      text: 'Your brood in corridor 7 is complete. It now is worth 2 when attacking or defending. And every round it isn’t attacking, it airdrops a new drone.' },
    { id: 'dropZone', round: 7, kind: 'act', highlight: [5], dropZone: [5], done: (s) => s.dropZones.length === 1,
      text: 'Your new brood in corridor 7 produced an egg. Tell the brood where to launch it. The egg will be sent there after the attacks this round.' },
    { id: 'submit7', round: 7, kind: 'submit',
      text: 'Submit.' },
    { id: 'landed', round: 7, kind: 'next',
      text: 'Your airdrop landed in corridor 5.' },

    // ── R8 ──
    { id: 'finale', round: 8, kind: 'next', highlight: [7],
      text: 'Round 8. Corridor 7 has 2 veterans and your brood: strength 4, and they have nothing there. The bigger the margin, the bigger the blast.' },
    { id: 'attack7', round: 8, kind: 'act', highlight: [7], attack: 7, done: (s) => s.board[6].isAttacking,
      text: 'Attack from corridor 7.' },
    { id: 'aim7', round: 8, kind: 'act', highlight: [7], aims: 'finale', aimCorridor: 7, done: aimed(7),
      text: 'Tap the hex the arrow points to, near B.' },
    { id: 'deploy8', round: 8, kind: 'act', highlight: [3, 4], deploy: [3, 4], done: deploysDone,
      text: 'Deploy to corridors 3 and 4.' },
    { id: 'submit8', round: 8, kind: 'submit',
      text: 'Submit.' },
    { id: 'won', round: 8, kind: 'end',
      text: 'The blast killed structures B and C: three structures down. You won the war.' },
]

// ── The machine ─────────────────────────────────────────────────────────────
// makeTutorialMachine(def) → { steps, targets, initialState, reduce,
// currentStep, stepText, canSubmit, owedDropZones } for one scripted
// tutorial: def = { steps, targets (aim key → hex), createWar () → war,
// playRound (war, roundIndex, selfOrders) → { result, submitted, war } }.
// Tutorial v2 and v3 (tutorialV3Steps.js) are both one.

export function makeTutorialMachine({ steps, targets, createWar, playRound }) {
    // ── State ───────────────────────────────────────────────────────────────────

    function planning(war) {
        return {
            board: war.p1.board,
            armiesToDeploy: ARMIES_PER_ROUND,
            targetPlots: noTargets(),
            targetSubplots: noTargets(),
            dropZones: [],
        }
    }

    function initialState() {
        const war = createWar()
        return { stepIndex: 0, roundIndex: 0, war, ...planning(war), aims: {}, tip: null, reveal: null, history: [] }
    }

    const currentStep = (state) => steps[state.stepIndex]
    const stepText = (state) => {
        const t = currentStep(state).text
        return typeof t === 'function' ? t(state) : t
    }

    // How many drop zones the plan owes (the live game's requiredDropZoneCount).
    function owedDropZones(state) {
        const open = state.board.reduce((n, bf) => n + Math.max(0, bf.maxArmyQty - (bf.armyQty || 0) - (bf.newArmies || 0) - (bf.movedInArmies || 0)), 0)
        return Math.min(requiredDropZones(state.board), open)
    }

    // Submit is live on a submit step once every drone and drop zone is placed.
    function canSubmit(state) {
        return currentStep(state).kind === 'submit' && state.armiesToDeploy === 0 && state.dropZones.length >= owedDropZones(state)
    }

    // ── The reducer ─────────────────────────────────────────────────────────────
    // tutorialV2Reduce(state, event) → state. Events:
    //   { type: 'next' }                          a next step's button
    //   { type: 'back' }                          the previous step, as it was
    //   { type: 'peek' }                          the eye was pressed
    //   { type: 'action', bfId, action, extras }  the board's onAction
    //   { type: 'aim', col, subS }                the board's onTargetPlot
    //   { type: 'dropZone', col, action }         the board's onDropZone
    //   { type: 'submit' }
    // A refused event leaves the step where it is and sets `tip`. A submit
    // returns the next state with `reveal` set ({ result, submitted, before }):
    // the screen plays it, then dispatches { type: 'revealed' } to clear it.

    const refuse = (state, tip) => ({ ...state, tip })

    function advance(state) {
        let s = state
        // An act step whose work is already done (it can't be, today, but a back
        // jump could land there) moves on by itself.
        do {
            s = { ...s, stepIndex: s.stepIndex + 1 }
        } while (currentStep(s).kind === 'act' && currentStep(s).done(s))
        return s
    }

    function remember(prev, next) {
        const { history, ...snapshot } = prev
        return { ...next, tip: null, history: [...history, snapshot] }
    }

    function onBoardAction(state, step, { bfId, action, extras }) {
        switch (action) {
            case 'deploy': {
                if (!step.deploy?.includes(bfId) || fresh(state, bfId) >= freshPlanned(step, bfId)) {
                    return refuse(state, step.deploy ? `Deploy to corridor ${[...new Set(step.deploy)].join(' and ')}.` : 'Not now: follow the step above.')
                }
                const r = deployArmy(state.board, bfId, state.armiesToDeploy)
                if (!r.success) return refuse(state, r.reason)
                return { ...state, board: r.boardArr, armiesToDeploy: r.armiesToDeploy }
            }
            case 'move': {
                if (!step.move || step.move.from !== bfId || step.move.dir !== extras?.direction) {
                    return refuse(state, step.move ? `Move from corridor ${step.move.from} to the ${step.move.dir}.` : 'Not now: follow the step above.')
                }
                const r = moveUnit(state.board, bfId, extras.direction, extras?.uid ?? null)
                if (!r.success) return refuse(state, r.reason)
                return { ...state, board: r.boardArr }
            }
            case 'attack': {
                if (step.attack !== bfId) return refuse(state, step.attack ? `Attack from corridor ${step.attack}.` : 'Not now: follow the step above.')
                const bf = state.board[bfId - 1]
                const v = canAttack(bf)
                if (!v.valid) return refuse(state, v.reason)
                return { ...state, board: state.board.map((b, i) => (i === bfId - 1 ? { ...b, isAttacking: true } : b)) }
            }
            case 'buildFort': {
                if (step.brood !== bfId) return refuse(state, 'Not now: follow the step above.')
                const centerS = extras?.centerS ?? null
                if (centerS == null || !canPlaceBase(state.war.p1.homefront, bfId - 1, centerS)) return refuse(state, 'Pick a clear row in corridor 7 for your brood.')
                const r = buildFort(state.board, bfId, centerS, extras?.uids ?? null)
                if (!r.success) return refuse(state, r.reason)
                return { ...state, board: r.boardArr }
            }
            default:
                // Undos and anything else: the tutorial keeps to its script.
                return refuse(state, 'Not now: follow the step above.')
        }
    }

    function reduce(state, event) {
        const step = currentStep(state)
        if (state.reveal && event.type !== 'revealed') return state

        switch (event.type) {
            case 'revealed':
                return { ...state, reveal: null }

            case 'back': {
                const prev = state.history[state.history.length - 1]
                if (!prev) return state
                return { ...prev, history: state.history.slice(0, -1), tip: null, reveal: null }
            }

            case 'next':
                if (step.kind !== 'next') return state
                return remember(state, advance(state))

            case 'peek':
                if (step.kind !== 'peek') return state
                return remember(state, advance(state))

            case 'action': {
                if (step.kind !== 'act') return refuse(state, step.kind === 'submit' ? 'Tap Submit.' : 'Read the step above, then tap Next.')
                // A tip left over from an earlier refusal must not read as this
                // action being refused.
                const next = onBoardAction({ ...state, tip: null }, step, event)
                if (next.tip) return next
                return remember(state, step.done(next) ? advance(next) : next)
            }

            case 'aim': {
                // A second tap on the hex already aimed at (a double tap, or a
                // re-tap to be sure) changes nothing: no refusal.
                if (state.targetSubplots[event.col] === event.subS) return state
                if (step.kind !== 'act' || !step.aims || event.col !== step.aimCorridor - 1) return refuse(state, 'Not now: follow the step above.')
                if (event.subS !== targets[step.aims]) return refuse(state, 'Tap the hex the arrow points to.')
                const targetPlots = [...state.targetPlots]; targetPlots[event.col] = plotOf(event.subS).row
                const targetSubplots = [...state.targetSubplots]; targetSubplots[event.col] = event.subS
                const next = { ...state, targetPlots, targetSubplots, aims: { ...state.aims, [step.aims]: event.subS } }
                return remember(state, advance(next))
            }

            case 'dropZone': {
                if (event.action !== 'place' || !step.dropZone?.includes(event.col + 1)) return refuse(state, step.dropZone ? `Place the drop zone on corridor ${step.dropZone[0]}.` : 'Not now: follow the step above.')
                const v = canPlaceDropZone(state.board, event.col, state.dropZones)
                if (!v.valid) return refuse(state, v.reason)
                const next = { ...state, dropZones: [...state.dropZones, event.col] }
                return remember(state, step.done(next) ? advance(next) : next)
            }

            case 'submit': {
                if (!canSubmit(state)) return refuse(state, step.kind === 'submit' ? 'Place every drone first.' : 'Not yet: follow the step above.')
                const orders = {
                    board: state.board, targetPlots: state.targetPlots, targetSubplots: state.targetSubplots,
                    dropZones: state.dropZones, rockets: [], drones: [],
                }
                const played = playRound(state.war, state.roundIndex, orders)
                const next = {
                    ...state,
                    war: played.war,
                    roundIndex: state.roundIndex + 1,
                    ...planning(played.war),
                    reveal: { result: played.result, submitted: played.submitted, before: state.war },
                }
                return remember(state, advance(next))
            }

            default:
                return state
        }
    }

    // What a player following the instructions does at the current step:
    // the events that complete it. Reveals are skipped (no screen plays them).
    function playStep(state) {
        const step = currentStep(state)
        switch (step.kind) {
            case 'next': return reduce(state, { type: 'next' })
            case 'peek': return reduce(state, { type: 'peek' })
            case 'submit': return reduce(reduce(state, { type: 'submit' }), { type: 'revealed' })
            case 'act': {
                if (step.attack) return reduce(state, { type: 'action', bfId: step.attack, action: 'attack' })
                if (step.aims) return reduce(state, { type: 'aim', col: step.aimCorridor - 1, subS: targets[step.aims] })
                if (step.move) return reduce(state, { type: 'action', bfId: step.move.from, action: 'move', extras: { direction: step.move.dir } })
                if (step.brood) {
                    const centerS = step.gesture?.centerS ?? subIndex((step.brood - 1) * 3 + 1, 4)
                    return reduce(state, { type: 'action', bfId: step.brood, action: 'buildFort', extras: { centerS } })
                }
                if (step.dropZone) return reduce(state, { type: 'dropZone', col: step.dropZone[0] - 1, action: 'place' })
                let s = state
                for (const c of step.deploy ?? []) {
                    if (s.stepIndex !== state.stepIndex) break
                    if ((s.board[c - 1].newArmies || 0) >= step.deploy.filter(x => x === c).length) continue
                    s = reduce(s, { type: 'action', bfId: c, action: 'deploy' })
                }
                return s
            }
            default: return state
        }
    }

    // A fresh tutorial played up to (not through) step `id`; the whole run
    // if no step has it. For reviewing a step without playing to it.
    function startAt(id) {
        let s = initialState()
        while (currentStep(s).id !== id && currentStep(s).kind !== 'end') {
            const next = playStep(s)
            if (next.stepIndex === s.stepIndex) break
            s = next
        }
        return { ...s, tip: null, history: [] }
    }

    return { steps, targets, initialState, reduce, currentStep, stepText, canSubmit, owedDropZones, playStep, startAt }
}

const V2 = makeTutorialMachine({
    steps: TUTORIAL_V2_STEPS, targets: TUTORIAL_V2_TARGETS, createWar: createTutorialV2War, playRound: playTutorialV2RoundWith,
})
export const TUTORIAL_V2_MACHINE = V2
export const initialTutorialV2State = V2.initialState
export const tutorialV2Reduce = V2.reduce
export const currentStep = V2.currentStep
export const stepText = V2.stepText
export const owedDropZones = V2.owedDropZones
export const canSubmitTutorialV2 = V2.canSubmit
